<template>
    <application-layout
        :property="property"
        :application="application"
        :experian_attempt="experian_attempt"
        :step_completion_status="$page.props.step_completion_status"
        :step_progress_data="$page.props.step_progress_data"
        :completed_steps_map="$page.props.completed_steps_map"
        current-step="payment"
        sub-title=" - Payment"
    >
        <div class="relative pb-6">
            <div class="bg-gray-50 rounded-lg shadow-md p-6">
                <!-- RAC-10476: itemised summary. The screening fee is shown in full on the
                     background-check line (or "Landlord Paid" when the landlord paid it);
                     the application itself is listed at $0. -->
                <div class="mb-6 p-4 bg-white rounded-lg border border-gray-200" data-test="payment-summary">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Payment Summary</h3>
                    <div class="space-y-2 text-sm">
                        <div class="flex justify-between gap-4 text-gray-700">
                            <span>Rental Application</span>
                            <span :class="amountAlignClass">$0.00</span>
                        </div>
                        <div class="flex justify-between gap-4 text-gray-700">
                            <span>Credit Check + Criminal &amp; Housing Search</span>
                            <span v-if="landlord_paid_screening" class="font-medium text-green-700" :class="amountAlignClass" data-test="landlord-paid">Landlord Paid</span>
                            <span v-else :class="amountAlignClass">${{ screening_fee }}</span>
                        </div>

                        <div
                            v-if="application_boost_in_cart || show_eso_option"
                            class="relative rounded-lg border border-amber-200 bg-amber-50 p-3"
                            data-test="application-boost"
                        >
                            <div class="flex items-start justify-between gap-4">
                                <div class="min-w-0">
                                    <p class="flex items-center gap-1.5 font-semibold text-amber-600">
                                        <font-awesome-icon :icon="faStars" aria-hidden="true" />
                                        Application Boost
                                    </p>
                                    <p class="text-gray-700">Income, Employment, Identity &amp; Asset Verification</p>
                                    <p class="text-xs text-gray-500 mt-0.5">Stand out from other applicants by verifying up front.</p>
                                </div>
                                <div class="flex shrink-0 items-start gap-3">
                                    <template v-if="application_boost_in_cart">
                                        <span class="text-gray-700">${{ eso_price }}</span>
                                        <button
                                            v-if="!boost_payment_processing"
                                            type="button"
                                            class="inline-flex h-5 w-5 items-center justify-center rounded text-gray-400 hover:bg-amber-100 hover:text-gray-600 disabled:opacity-50"
                                            aria-label="Remove Application Boost"
                                            :disabled="removingEso"
                                            @click="showRemoveBoostConfirm = true"
                                        >
                                            <font-awesome-icon :icon="faXmark" class="h-4 w-4" aria-hidden="true" />
                                        </button>
                                    </template>
                                    <button
                                        v-else
                                        type="button"
                                        class="inline-flex items-center rounded-md bg-amber-200 px-3 py-1.5 text-xs font-semibold text-gray-900 hover:bg-amber-300 disabled:opacity-50"
                                        :disabled="addingEso"
                                        @click="addEso"
                                    >
                                        {{ addingEso ? 'Adding...' : `Add for $${eso_price}` }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="border-t border-gray-200 pt-2 flex justify-between font-semibold text-gray-900">
                            <span>Total</span>
                            <span :class="amountAlignClass">${{ amount }}</span>
                        </div>
                    </div>
                </div>
                <alert-card v-if="boost_payment_processing" :type="'info'" data-test="boost-payment-processing">
                    Your Application Boost payment is still processing. Please refresh this page in a moment.
                </alert-card>
                <stripe-form
                    v-else
                    :publishable_key="publishable_key"
                    :payment_intent="payment_intent"
                    :user="user"
                    :amount="amount"
                    :first_name="first_name"
                    :last_name="last_name"
                    :billing_type="billing_type"
                    :report_id="report_id"
                    :application_id="application_id"
                    :report-payment="false"
                    :acknowledgment-url="route('application.purchase-acknowledgment', application.id)"
                    :acknowledgment-text="purchase_acknowledgment_text"
                    :return-url="return_url"
                />
                <!-- RAC-10476: on the Boost-only checkout the Boost is all there is to buy, so
                     say plainly that the applicant can carry on without it. Same nudge as the X. -->
                <div v-if="landlord_paid_screening && !boost_payment_processing" class="mt-4 text-center">
                    <button
                        type="button"
                        class="text-sm text-gray-500 underline hover:text-gray-700 disabled:opacity-50"
                        data-test="continue-without-boost"
                        :disabled="removingEso"
                        @click="showRemoveBoostConfirm = true"
                    >
                        Continue without Application Boost
                    </button>
                </div>
            </div>
        </div>

        <!-- RAC-10476: one last nudge before the boost leaves the cart. Closing the
             dialog any other way keeps it. -->
        <TransitionRoot as="template" :show="showRemoveBoostConfirm">
            <HeadlessDialog as="div" class="relative z-50" @close="keepBoost">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>
                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white px-6 py-8 text-center shadow-xl transition-all" data-test="remove-boost-confirm">
                                <DialogTitle as="h3" class="text-2xl font-bold text-gray-900">Are you sure?</DialogTitle>
                                <p class="mt-3 text-lg text-gray-700">Verifying your application is a great way to stand out.</p>
                                <div class="mt-6 flex flex-col items-center gap-3">
                                    <button
                                        type="button"
                                        class="rounded-md bg-amber-200 px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-amber-300"
                                        @click="keepBoost"
                                    >
                                        Yes, keep application boost
                                    </button>
                                    <button
                                        type="button"
                                        class="text-sm text-gray-600 hover:text-gray-900"
                                        @click="confirmRemoveBoost"
                                    >
                                        No thanks
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </HeadlessDialog>
        </TransitionRoot>
    </application-layout>
</template>

<script>
import ApplicationLayout from './ApplicationLayout.vue';
import StripeForm from '@/Components/StripeForm.vue';
import AlertCard from '@/Shared/AlertCard.vue';
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import { router } from "@inertiajs/vue3";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faStars, faXmark } from '@fortawesome/pro-solid-svg-icons';

export default {
    components: {
        ApplicationLayout,
        StripeForm,
        AlertCard,
        HeadlessDialog: Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        FontAwesomeIcon,
    },
    props: {
        publishable_key: String,
        amount: String,
        first_name: String,
        last_name: String,
        billing_type: String,
        report_id: String,
        application_id: String,
        user: Object,
        payment_intent: Object,
        property: Object,
        application: Object,
        experian_attempt: Object,
        applicant_eso_opted_in: {
            type: Boolean,
            default: false,
        },
        eso_price: {
            type: String,
            default: '0.00',
        },
        show_eso_option: {
            type: Boolean,
            default: false,
        },
        screening_fee: {
            type: String,
            default: '0.00',
        },
        application_boost_in_cart: {
            type: Boolean,
            default: false,
        },
        // RAC-10476: the landlord paid the application fee; only the Boost is charged.
        landlord_paid_screening: {
            type: Boolean,
            default: false,
        },
        purchase_acknowledgment_text: {
            type: String,
            default: '',
        },
        return_url: {
            type: String,
            default: null,
        },
        boost_payment_processing: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            removingEso: false,
            addingEso: false,
            showRemoveBoostConfirm: false,
        };
    },
    computed: {
        /**
         * Shifts the summary amounts left so they line up with the boost price, which sits
         * inside the boost card's border + padding (13px) and left of its w-5 X button + gap (32px).
         */
        amountAlignClass() {
            return this.application_boost_in_cart ? 'pr-[45px]' : '';
        },
    },
    methods: {
        keepBoost() {
            this.showRemoveBoostConfirm = false;
        },
        confirmRemoveBoost() {
            this.showRemoveBoostConfirm = false;
            this.removeEso();
        },
        removeEso() {
            this.removingEso = true;
            router.post(route('application.remove-eso', this.application.id), {}, {
                onFinish: () => {
                    this.removingEso = false;
                },
            });
        },
        addEso() {
            this.addingEso = true;
            router.post(route('application.add-eso', this.application.id), {}, {
                onFinish: () => {
                    this.addingEso = false;
                },
            });
        },
    },
    setup(props) {
        const user = computed(() => usePage().props.auth.user);
        return {user, faStars, faXmark};
    },
}
</script>
